/**
 * Create with AI — description on the left; suggested props + literature rationale on the right.
 */
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { scenariosApi } from '@/api/scenarios';
import type { MbPropAiRationale } from '@/types/materialBalance';
import {
  applyMbPropsAiSuggestion,
  MB_PROP_ROWS,
  mbPropsFormToContext,
  mbPropsOverwriteKeys,
  type MbPropsAiApplyMode,
  type MbPropKey,
  type MbPropsForm,
} from '@/utils/materialBalanceProps';

export interface MaterialBalancePropsAiDialogProps {
  open: boolean;
  onClose: () => void;
  scenarioId: string;
  layerId: string;
  layerName: string;
  propsForm: MbPropsForm;
  onApply: (next: MbPropsForm) => void;
  readOnly?: boolean;
}

function apiErrorDetail(err: unknown): string {
  if (!err) return 'Request failed';
  if (typeof err === 'string') return err;
  const anyErr = err as {
    message?: string;
    detail?: unknown;
    response?: { data?: { detail?: unknown } };
  };
  const detail =
    anyErr?.response?.data?.detail ?? anyErr?.detail ?? anyErr?.message;
  if (typeof detail === 'string') return detail;
  if (detail != null) {
    try {
      return JSON.stringify(detail);
    } catch {
      return 'Request failed';
    }
  }
  return 'Request failed';
}

function formatPropValue(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs !== 0 && (abs < 0.001 || abs >= 10000)) return n.toExponential(3);
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toPrecision(5)));
}

const PROP_META = Object.fromEntries(
  MB_PROP_ROWS.map((r) => [r.key, { label: r.label, unit: r.unit }]),
) as Record<string, { label: string; unit: string }>;

export const MaterialBalancePropsAiDialog: React.FC<
  MaterialBalancePropsAiDialogProps
> = ({
  open,
  onClose,
  scenarioId,
  layerId,
  layerName,
  propsForm,
  onApply,
  readOnly = false,
}) => {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggested, setSuggested] = useState<Record<string, number>>({});
  const [rationales, setRationales] = useState<Record<string, MbPropAiRationale>>(
    {},
  );
  const [modelNotes, setModelNotes] = useState<string | null>(null);
  const [conflictOpen, setConflictOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoading(false);
    setSuggested({});
    setRationales({});
    setModelNotes(null);
    setConflictOpen(false);
  }, [open]);

  const overwriteKeys = useMemo(
    () => mbPropsOverwriteKeys(propsForm, suggested),
    [propsForm, suggested],
  );

  const suggestedRows = useMemo(() => {
    const keys = Object.keys(suggested).filter(
      (k) => typeof suggested[k] === 'number' && Number.isFinite(suggested[k]),
    ) as MbPropKey[];
    const order = MB_PROP_ROWS.map((r) => r.key);
    keys.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
    });
    return keys.map((key) => ({
      key,
      value: suggested[key],
      meta: PROP_META[key] ?? { label: key, unit: '' },
      rationale: rationales[key],
    }));
  }, [suggested, rationales]);

  const canGenerate =
    !readOnly && !loading && description.trim().length > 0;
  const hasResults = suggestedRows.length > 0;

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const applyMode = (mode: MbPropsAiApplyMode) => {
    const next = applyMbPropsAiSuggestion(propsForm, suggested, mode);
    onApply(next);
    setConflictOpen(false);
    onClose();
  };

  const requestApply = () => {
    if (!hasResults) return;
    if (overwriteKeys.length > 0) {
      setConflictOpen(true);
      return;
    }
    applyMode('replace');
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError(null);
    setConflictOpen(false);
    try {
      const res = await scenariosApi.suggestMaterialBalancePropsAi(
        scenarioId,
        layerId,
        {
          description: description.trim(),
          current_props: mbPropsFormToContext(propsForm),
        },
      );
      setSuggested(res.props || {});
      setRationales(res.rationales || {});
      setModelNotes(res.notes ?? null);
      if (!res.props || Object.keys(res.props).length === 0) {
        setError('No usable property values in the suggestion');
      }
    } catch (err) {
      setError(apiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: { md: 'min(860px, 92vh)' } } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          Properties — Create with AI
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ ml: 1, fontWeight: 400 }}
          >
            {layerName}
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            pt: 1.5,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 0.9fr) 1.4fr' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            <Stack spacing={1.5} sx={{ minHeight: 0 }}>
              <Typography variant="body2" color="text.secondary">
                Describe geology, fluids, and development in one place. The assistant
                suggests property values with a short rationale and literature
                citations where possible.
              </Typography>
              <TextField
                label="Description"
                placeholder={
                  'e.g. North Sea Jurassic sandstone (Volve-like), ~2500–3000 m TVD, ' +
                  'moderate porosity/permeability, light oil, pattern waterflood with ' +
                  'peripheral support, weak aquifer…'
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                fullWidth
                required
                disabled={loading || readOnly}
                autoFocus
                sx={{
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start',
                  },
                  '& textarea': {
                    height: '100% !important',
                    overflow: 'auto !important',
                  },
                }}
                inputProps={{ style: { minHeight: 280 } }}
              />
              {error ? <Alert severity="error">{error}</Alert> : null}
              {modelNotes && hasResults ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {modelNotes}
                </Alert>
              ) : null}
            </Stack>

            <Box
              sx={{
                minWidth: 0,
                minHeight: 320,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="subtitle2">
                  Suggested properties
                  {hasResults ? ` (${suggestedRows.length})` : ''}
                </Typography>
              </Box>
              {!hasResults && !loading ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Run Generate to fill this table with values and literature-backed
                    justifications.
                  </Typography>
                </Box>
              ) : null}
              {loading ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    p: 3,
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Generating suggestions…
                  </Typography>
                </Box>
              ) : null}
              {hasResults && !loading ? (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '18%' }}>Property</TableCell>
                        <TableCell align="right" sx={{ width: '14%' }}>
                          Value
                        </TableCell>
                        <TableCell sx={{ width: '10%' }}>Unit</TableCell>
                        <TableCell>Rationale & references</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {suggestedRows.map((row) => (
                        <TableRow key={row.key} hover>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" fontWeight={600}>
                              {row.meta.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.key}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              verticalAlign: 'top',
                              fontVariantNumeric: 'tabular-nums',
                              fontWeight: 600,
                            }}
                          >
                            {formatPropValue(row.value)}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="caption" color="text.secondary">
                              {row.meta.unit || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" sx={{ mb: 0.75 }}>
                              {row.rationale?.rationale ||
                                'Estimated from the description.'}
                            </Typography>
                            {(row.rationale?.references?.length ?? 0) > 0 ? (
                              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                                {row.rationale!.references!.map((ref, i) => (
                                  <Typography
                                    key={`${row.key}-ref-${i}`}
                                    component="li"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {ref.url ? (
                                      <Link
                                        href={ref.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.25,
                                        }}
                                      >
                                        {ref.citation}
                                        <OpenInNewIcon sx={{ fontSize: 11 }} />
                                      </Link>
                                    ) : (
                                      ref.citation
                                    )}
                                  </Typography>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                No literature link returned for this value.
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            onClick={() => void handleGenerate()}
            disabled={!canGenerate}
            startIcon={
              loading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AutoAwesomeIcon sx={{ fontSize: 16 }} />
              )
            }
          >
            {loading ? 'Generating…' : hasResults ? 'Regenerate' : 'Generate'}
          </Button>
          <Button
            variant="contained"
            onClick={requestApply}
            disabled={loading || !hasResults || readOnly}
          >
            Apply to form
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={conflictOpen} onClose={() => setConflictOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply AI properties?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The form already has values for {overwriteKeys.length} of{' '}
            {suggestedRows.length} suggested parameters. How should we apply the
            result?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setConflictOpen(false)}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" onClick={() => applyMode('fill_missing')}>
            Fill missing only
          </Button>
          <Button variant="contained" onClick={() => applyMode('replace')}>
            Replace all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
